import {createApi} from '@reduxjs/toolkit/query/react';
import {customAxiosBaseQuery} from '../API/customAxiosBaseQuery';
import {
	OrderInPaperworkType,
	RequestPaperworkHistoryType,
	RequestPaperworkListType,
	ResponseEditOrderType,
	ResponseOrderType,
	ResponsePaperworkHistoryType,
	ResponsePaperworkListType,
} from '../types/TypesDepotPaperwork';
import {storeActivitiesAPI, storeCashierAPI} from '../API/api';
import {BaseResponseType, RequestCreateDesignType} from '../types/TypesStore';
import {
	RequestCreateReceiptReturnType,
	RequestCreateReceiptType,
	RequestPrintReceiptType,
} from '../types/CashierSliceStateType';

export const activitiesApi = createApi({
	reducerPath: 'activitiesApi',
	baseQuery: customAxiosBaseQuery,
	tagTypes: ['paperwork-list', 'paperwork-history', 'edit-design-info'],
	endpoints: (builder) => ({
		paperworkList: builder.query<
			ResponsePaperworkListType,
			RequestPaperworkListType
		>({
			query: (data) => ({
				func: storeActivitiesAPI.getPaperworkList,
				args: [data],
			}),
			serializeQueryArgs: ({endpointName, queryArgs}) => {
				const {offset, ...filters} = (queryArgs ||
					{}) as RequestPaperworkListType;
				return `${endpointName}:${JSON.stringify(filters)}`;
			},
			merge: (currentCache, newItems) => {
				const prevOrders = currentCache.orders ?? [];
				const newOrders = newItems.orders ?? [];
				// объединяем заказы уникально по id
				const map = new Map<string, OrderInPaperworkType>();
				for (const o of prevOrders) map.set(o.id, o);
				for (const o of newOrders) map.set(o.id, o);
				currentCache.orders = Array.from(map.values());

				// корректно суммируем агрегаты
				const prevCount = Number(currentCache.count) || 0;
				const prevPoints = Number(currentCache.sum_points) || 0;
				const prevMoney = Number(currentCache.sum_money) || 0;

				const newCount = Number(newItems.count) || 0;
				const newPoints = Number(newItems.sum_points) || 0;
				const newMoney = Number(newItems.sum_money) || 0;

				currentCache.count = prevCount + newCount;
				currentCache.sum_points = (prevPoints + newPoints).toFixed(2);
				currentCache.sum_money = (prevMoney + newMoney).toFixed(2);

				// валюту берём последнюю
				currentCache.currency = newItems.currency ?? currentCache.currency;
			},

			forceRefetch({currentArg, previousArg}) {
				return (
					currentArg?.offset !== previousArg?.offset ||
					JSON.stringify(currentArg) !== JSON.stringify(previousArg)
				);
			},

			providesTags: ['paperwork-list'],
		}),

		paperworkHistory: builder.query<
			ResponsePaperworkHistoryType,
			RequestPaperworkHistoryType
		>({
			query: (data) => ({
				func: storeActivitiesAPI.getPaperworkHistory,
				args: [data],
			}),
			providesTags: ['paperwork-history'],
		}),
		createReceipt: builder.mutation<
			BaseResponseType & {receipt_id: number},
			RequestCreateReceiptType
		>({
			query: (data) => ({
				func: storeCashierAPI.getCreateReceipt,
				args: [data],
			}),
			invalidatesTags: ['paperwork-list'],
		}),
		createReturnReceipt: builder.mutation<
			BaseResponseType & {receipt_id: number},
			RequestCreateReceiptReturnType
		>({
			query: (data) => ({
				func: storeCashierAPI.getCreateReturnReceipt,
				args: [data],
			}),
			invalidatesTags: ['paperwork-list'],
		}),
		printReceipt: builder.mutation<
			BaseResponseType & {html: string},
			RequestPrintReceiptType
		>({
			query: (data) => ({
				func: storeCashierAPI.getPrintReceipt,
				args: [data],
			}),
			invalidatesTags: ['paperwork-list'],
		}),

		createActivities: builder.mutation<
			BaseResponseType,
			RequestCreateDesignType
		>({
			query: (data) => ({
				func: storeActivitiesAPI.getCreateActivities,
				args: [data],
			}),
		}),
		deleteActivities: builder.mutation<BaseResponseType, number>({
			query: (data) => ({
				func: storeActivitiesAPI.deleteActivities,
				args: [data],
			}),
		}),
		editActivities: builder.query<ResponseEditOrderType, number>({
			query: (data) => ({
				func: storeActivitiesAPI.editActivities,
				args: [data],
			}),
			providesTags: ['edit-design-info'],
		}),
		printActivities: builder.mutation<ResponseOrderType, number>({
			query: (data) => ({
				func: storeActivitiesAPI.printActivities,
				args: [data],
			}),
		}),
	}),
});

export const {
	usePaperworkListQuery,
	usePaperworkHistoryQuery,
	useDeleteActivitiesMutation,
	useLazyEditActivitiesQuery,
	usePrintActivitiesMutation,
	useCreateReceiptMutation,
	usePrintReceiptMutation,
	useCreateReturnReceiptMutation,
	useCreateActivitiesMutation,
} = activitiesApi;
